import { Injectable } from "@angular/core";
import { AlulaDevice, AlulaDeviceHelper } from "@app/project/model/alula-device";
import { AlulaUser } from "@app/project/model/alula-user";
import { AlulaDatabaseService, AlulaResponseData } from "@app/shared/databases/alula.database";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DataServiceError } from "../../shared/services/data-service-error";
@Injectable({
    providedIn: "root",
})
export class AlulaDeviceDbService {
    private deviceEndPoint = "devices";

    getDeviceByMacAddress({ macAddress: mac } = { macAddress: "" }): Observable<AlulaDevice> {
        return this.alulaDatabaseService.getRecords$(this.deviceEndPoint, { mac }).pipe(
            map(devices => devices.filter(device => device.attributes.mac)),
            map((devices: AlulaResponseData[] = []) => (devices.length ? AlulaDevice.fromDbObject(devices[0]) : null)),
            catchError(DataServiceError.handleError())
        );
    }

    getDevicesByReceiverGroupId(receiverGroupId: string) {
        return this.alulaDatabaseService.getRecords$(this.deviceEndPoint, { receiverGroupId }).pipe(
            map((devices = []) => devices.map(device => AlulaDevice.fromDbObject(device))),
            catchError(DataServiceError.handleError())
        );
    }

    getARNDevices(dealerId: string) {
        return this.alulaDatabaseService.getRecords$(this.deviceEndPoint, { dealerId, receiverGroupId: null }).pipe(
            map((devices = []) => devices.map(device => AlulaDevice.fromDbObject(device))),
            catchError(DataServiceError.handleError())
        );
    }

    logEvent(action: string, rawBodyContent: Record<any, any>) {
        this.tsLoggingService.logEvent(null, "alula device", "ssopgrest", action, null, rawBodyContent);
    }

    createAlulaDevice(device: AlulaDevice, dealerID: string): Observable<AlulaDevice> {
        const rawBodyContent = {
            device,
            dealerID,
        };

        this.logEvent("add", rawBodyContent);
        return this.alulaDatabaseService
            .createRecordRpc$("devices/register", {
                crc: device.crcCode,
                dealerId: dealerID,
                mac: device.macAddress,
                skipPartnerClaim: true,
            })
            .pipe(
                map(newDevice => {
                    device.id = newDevice.data.id;
                    return device;
                }),
                catchError(err => {
                    console.log(err);
                    return DataServiceError.handleError(device)(err);
                })
            );
    }

    updateAlulaDeviceProperties(device: AlulaDevice): Observable<AlulaDevice> {
        const rawBodyContent = {
            device: AlulaDeviceHelper.exportDeviceProperties(device),
        };

        this.logEvent("update", rawBodyContent);
        return this.alulaDatabaseService.updateRecord$(this.deviceEndPoint, rawBodyContent.device, device.id).pipe(
            map(_device => AlulaDevice.fromDbObject(_device)),
            catchError(DataServiceError.handleError(device))
        );
    }

    updateSelectAlulaDeviceProperties(device: AlulaDevice): Observable<AlulaDevice> {
        this.logEvent("update", { device });
        return this.alulaDatabaseService.updateRecord$(this.deviceEndPoint, device, device.id).pipe(
            map(_device => AlulaDevice.fromDbObject(_device)),
            catchError(DataServiceError.handleError(device))
        );
    }

    upsertAlulaDevice(device: AlulaDevice, dealerId: string): Observable<AlulaDevice> {
        // According to Alula, we can't update devices until after they are assigned to customers
        // so now we don't upsert here, only insert
        return !device.id ? this.createAlulaDevice(device, dealerId) : of(device);
    }

    assignDeviceToCustomer({ user, device } = { user: {} as AlulaUser, device: {} as AlulaDevice }): Observable<any> {
        const deviceCustomerId = device.customerId;
        const userId = user.id;

        if (deviceCustomerId && userId !== deviceCustomerId) {
            return DataServiceError.handleError()({
                message: "Update failed. This security device is associated with another customer.",
            });
        }
        if (!deviceCustomerId) {
            const rawBodyContent = {
                device,
                user,
            };

            this.logEvent("assign", rawBodyContent);
            return this.alulaDatabaseService.updateRecordRpc$("devices/assign", {
                deviceId: device.id,
                customerId: userId,
            });
        }
        return of(null);
    }

    unassignDeviceFromCustomer(device: AlulaDevice): Observable<any> {
        const rawBodyContent = {
            device,
        };

        this.logEvent("unassign", rawBodyContent);
        return this.alulaDatabaseService.updateRecordRpc$("devices/unassign", {
            deviceId: device.id,
        });
    }

    unregisterDevice(alulaDevice: AlulaDevice): Observable<any> {
        //when doing disable the monitoring service
        //check if device is registered and unassigned from customer
        //then unregister the device
        this.getDeviceByMacAddress({
            macAddress: alulaDevice.macAddress,
        }).subscribe(device => {
            if (device?.dealerId === alulaDevice.dealerId && device?.customerId === "") {
                const rawBodyContent = {
                    alulaDevice,
                };
                this.logEvent("unregister", rawBodyContent);
                return this.alulaDatabaseService.updateRecordRpc$("devices/unregister", {
                    deactivate: true,
                    mac: alulaDevice.macAddress,
                });
            }
        });
        return of(null);
    }
    constructor(private alulaDatabaseService: AlulaDatabaseService, private tsLoggingService: TSLoggingService) {}
}
